import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, CheckCircle, XCircle, Image as ImageIcon, Video, ExternalLink, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const CreativeManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [creatives, setCreatives] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    campaign_id: '',
    creative_name: '',
    width: '',
    height: '',
    alt_text: '',
    click_url: '',
    file: null
  });
  
  const [approvalData, setApprovalData] = useState({
    approval_status: 'approved',
    approval_notes: ''
  });

  useEffect(() => {
    fetchCampaigns();
    fetchCreatives();
  }, [filterCampaign, filterStatus]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchCreatives = async () => {
    try {
      setLoading(true);
      let url = `${backendUrl}/api/admin/creatives`;
      const params = [];
      if (filterCampaign !== 'all') params.push(`campaign_id=${filterCampaign}`);
      if (filterStatus !== 'all') params.push(`approval_status=${filterStatus}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setCreatives(response.data || []);
    } catch (error) {
      console.error('Error fetching creatives:', error);
      toast({
        title: 'Error',
        description: 'Failed to load creatives',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Please upload an image (JPG, PNG, GIF, WebP) or video (MP4, WebM)',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file size (5MB for videos, 150KB for images)
      const maxSize = file.type.startsWith('video/') ? 5 * 1024 * 1024 : 150 * 1024;
      if (file.size > maxSize) {
        toast({
          title: 'File Too Large',
          description: `File size should be less than ${file.type.startsWith('video/') ? '5MB' : '150KB'}`,
          variant: 'destructive'
        });
        return;
      }
      
      setFormData({ ...formData, file });
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      toast({
        title: 'Error',
        description: 'Please select a file to upload',
        variant: 'destructive'
      });
      return;
    }
    
    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', formData.file);
      uploadFormData.append('campaign_id', formData.campaign_id);
      uploadFormData.append('creative_name', formData.creative_name);
      uploadFormData.append('width', formData.width);
      uploadFormData.append('height', formData.height);
      uploadFormData.append('alt_text', formData.alt_text);
      uploadFormData.append('click_url', formData.click_url);
      
      await axios.post(`${backendUrl}/api/admin/campaigns/${formData.campaign_id}/creatives`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast({ title: 'Success', description: 'Creative uploaded successfully' });
      setDialogOpen(false);
      resetForm();
      fetchCreatives();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload creative',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleApproval = async () => {
    try {
      await axios.put(
        `${backendUrl}/api/admin/creatives/${selectedCreative.id}/approve`,
        approvalData
      );
      
      toast({
        title: 'Success',
        description: `Creative ${approvalData.approval_status} successfully`
      });
      
      setApprovalDialogOpen(false);
      setSelectedCreative(null);
      setApprovalData({
        approval_status: 'approved',
        approval_notes: ''
      });
      fetchCreatives();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process approval',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (creativeId) => {
    if (!window.confirm('Are you sure you want to archive this creative? It can be restored later.')) return;
    
    try {
      await axios.delete(`${backendUrl}/api/admin/creatives/${creativeId}`);
      toast({ title: 'Success', description: 'Creative archived successfully' });
      fetchCreatives();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete creative',
        variant: 'destructive'
      });
    }
  };

  const openApprovalDialog = (creative) => {
    setSelectedCreative(creative);
    setApprovalDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      campaign_id: '',
      creative_name: '',
      width: '',
      height: '',
      alt_text: '',
      click_url: '',
      file: null
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getFileTypeIcon = (fileType) => {
    if (fileType.startsWith('video/')) {
      return <Video className="text-purple-600" size={40} />;
    }
    return <ImageIcon className="text-blue-600" size={40} />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Creative Management</h1>
          <p className="text-gray-600 mt-1">Manage advertising creative assets</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus size={16} className="mr-2" />
              Upload Creative
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload New Creative</DialogTitle>
              <DialogDescription>
                Upload a new creative asset for a campaign
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="campaign_id">Campaign *</Label>
                <Select
                  value={formData.campaign_id}
                  onValueChange={(value) => setFormData({ ...formData, campaign_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.campaign_name} - {campaign.brand_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="creative_name">Creative Name *</Label>
                <Input
                  id="creative_name"
                  name="creative_name"
                  value={formData.creative_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Summer Banner 728x90"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="file">Upload File *</Label>
                <Input
                  id="file"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/webm"
                  onChange={handleFileChange}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Images: JPG, PNG, GIF, WebP (max 150KB) | Videos: MP4, WebM (max 5MB)
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="width">Width (px) *</Label>
                  <Input
                    id="width"
                    name="width"
                    type="number"
                    min="1"
                    value={formData.width}
                    onChange={handleInputChange}
                    placeholder="728"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="height">Height (px) *</Label>
                  <Input
                    id="height"
                    name="height"
                    type="number"
                    min="1"
                    value={formData.height}
                    onChange={handleInputChange}
                    placeholder="90"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="alt_text">Alt Text *</Label>
                <Input
                  id="alt_text"
                  name="alt_text"
                  value={formData.alt_text}
                  onChange={handleInputChange}
                  placeholder="Descriptive text for accessibility"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="click_url">Click URL *</Label>
                <Input
                  id="click_url"
                  name="click_url"
                  type="url"
                  value={formData.click_url}
                  onChange={handleInputChange}
                  placeholder="https://brand.com/landing-page"
                  required
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload Creative'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex gap-2 items-center">
              <Label>Campaign:</Label>
              <Select value={filterCampaign} onValueChange={setFilterCampaign}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.campaign_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 items-center">
              <Label>Status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Creatives Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      ) : creatives.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No creatives found</p>
            <p className="text-gray-500 text-sm mt-2">Upload your first creative to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {creatives.map((creative) => (
            <Card key={creative.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    {getFileTypeIcon(creative.file_type)}
                    <div>
                      <h3 className="font-semibold text-gray-900">{creative.creative_name}</h3>
                      <p className="text-xs text-gray-600">{creative.campaign_name}</p>
                    </div>
                  </div>
                  {getStatusBadge(creative.approval_status)}
                </div>
                
                {/* Preview */}
                <div className="bg-gray-100 rounded mb-3 flex items-center justify-center overflow-hidden" style={{ height: '200px' }}>
                  {creative.file_type.startsWith('image/') ? (
                    <img
                      src={`${backendUrl}${creative.file_url}`}
                      alt={creative.alt_text}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <video
                      src={`${backendUrl}${creative.file_url}`}
                      className="max-w-full max-h-full object-contain"
                      controls
                      muted
                    />
                  )}
                </div>
                
                {/* Details */}
                <div className="space-y-2 text-sm mb-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Brand:</span>
                    <span className="font-medium">{creative.brand_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dimensions:</span>
                    <span className="font-medium">{creative.width}x{creative.height}px</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Size:</span>
                    <span className="font-medium">{(creative.file_size / 1024).toFixed(1)} KB</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Click URL:</span>
                    <a
                      href={creative.click_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2c3e7d] hover:underline flex items-center gap-1"
                    >
                      <span className="truncate max-w-[100px]">View</span>
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
                
                {creative.approval_notes && (
                  <div className="mb-3 p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">
                      <strong>Notes:</strong> {creative.approval_notes}
                    </p>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-2">
                  {creative.approval_status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openApprovalDialog(creative)}
                      className="flex-1"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      Review
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(creative.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Creative: {selectedCreative?.creative_name}</DialogTitle>
            <DialogDescription>
              Approve or reject this creative asset
            </DialogDescription>
          </DialogHeader>
          
          {selectedCreative && (
            <div className="space-y-4 mt-4">
              {/* Preview */}
              <div className="bg-gray-100 rounded p-4 flex items-center justify-center" style={{ height: '200px' }}>
                {selectedCreative.file_type.startsWith('image/') ? (
                  <img
                    src={`${backendUrl}${selectedCreative.file_url}`}
                    alt={selectedCreative.alt_text}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <video
                    src={`${backendUrl}${selectedCreative.file_url}`}
                    className="max-w-full max-h-full object-contain"
                    controls
                    muted
                  />
                )}
              </div>
              
              <div>
                <Label>Decision</Label>
                <Select
                  value={approvalData.approval_status}
                  onValueChange={(value) => setApprovalData({ ...approvalData, approval_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approve</SelectItem>
                    <SelectItem value="rejected">Reject</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Input
                  value={approvalData.approval_notes}
                  onChange={(e) => setApprovalData({ ...approvalData, approval_notes: e.target.value })}
                  placeholder="Add any notes about this decision"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleApproval}>
                  Submit Decision
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default CreativeManagement;
